import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useContext, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { LoaderContext } from "components/context/LoaderContext";
import UserPersonalInfoForm from "components/organisms/UserPersonalInfoForm";
import UserAddressForm from "components/organisms/UserAddressForm";
import { UserContext, useUserContext } from "components/context/UserContext";
import useBlurHashEncoder from "components/hooks/useBlurHashEncoder";
import { Helmet } from "react-helmet-async";
import UserChangePasswordForm from "components/organisms/UserChangePasswordForm";
import { getChangePasswordURL } from "apis/user";
import { ROUTES, TOAST_CONFIG, UPLOAD_TO_S3, USER_SELF } from "../../constants";
import { patch, post, put } from "../../utils/request";
import {
  prependHTTPsForSocialLinks,
  appendJsonToFormData,
  handleSetErrorAndSwitchFormState,
  removeHTTPsForSocialLinks,
  validationForURLPattern,
  PasswordSchema,
} from "../../utils/HelperFunctions";
import UserSettingsPage from "./UserSettingsPage";

export type UpdatePassword = {
  old_password: string;
  new_password: string;
  confirm_new_password: string;
};

export type UserProfileSettingForm = {
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  twitter_link: string;
  instagram_link: string;
  other_link: string;
  address_line1: string;
  address_line2: string;
  country: string;
  state: string;
  city: string;
  zipcode: string;
  profile_image: any;
  profile_image_url: string | null;
  profile_image_hash: string | null;
} & UpdatePassword;

type FormFieldsType =
  | "address_line1"
  | "address_line2"
  | "country"
  | "state"
  | "city"
  | "zipcode"
  | "profile_image_url"
  | "first_name"
  | "last_name"
  | "username"
  | "email"
  | "twitter_link"
  | "instagram_link"
  | "other_link";

// formfields in reverse order of form
const formFields = {
  address_line1: "1",
  address_line2: "1",
  country: "1",
  state: "1",
  city: "1",
  zipcode: "1",
  profile_image_url: "0",
  first_name: "0",
  last_name: "0",
  username: "0",
  email: "0",
  twitter_link: "0",
  instagram_link: "0",
  other_link: "0",
};

const schema = [
  yup.object().shape({
    first_name: yup.string().trim(),
    last_name: yup.string().trim(),
    username: yup.string().trim().required("* Required field"),
    email: yup.string().trim().required("* Required field").email(),
    twitter_link: validationForURLPattern("twitter_link"),
    instagram_link: validationForURLPattern("instagram_link"),
    other_link: validationForURLPattern("other_link"),
  }),
  yup.object().shape({
    address_line1: yup.string().trim().nullable(),
    address_line2: yup.string().trim().nullable(),
    country: yup.string().trim().nullable(),
    state: yup.string().trim().nullable(),
    city: yup.string().trim().nullable(),
    zipcode: yup.string().trim().max(15, "Zip Code Cannot Be Longer Than 15 Characters").nullable(),
  }),
  yup.object().shape({
    old_password: yup.string().required("Please enter old password"),
    new_password: PasswordSchema,
    confirm_new_password: yup
      .string()
      .required("Please re-enter your password")
      .oneOf([yup.ref("new_password")], "Passwords doesn't match"),
  }),
];

function UserProfileSetting({
  currentStep,
  handleSetFormStep,
  navigate,
  defaultValues,
  reloadUserData,
}: {
  currentStep: number;
  handleSetFormStep: (step: string, replace?: boolean) => void;
  navigate: any;
  defaultValues: any;
  reloadUserData: () => void;
}) {
  const { setTokenId } = useUserContext();
  const { setLoading } = useContext(LoaderContext);
  const formMethods = useForm<UserProfileSettingForm>({
    resolver: yupResolver(schema[currentStep]),
    defaultValues,
    mode: "onChange",
  });

  const { handleSubmit, setError, trigger, watch } = formMethods;

  const getForm = (step: Number) => {
    switch (step) {
      case 0:
        return <UserPersonalInfoForm />;
      case 1:
        return <UserAddressForm />;
      case 2:
        return <UserChangePasswordForm />;
      default:
        return <UserPersonalInfoForm />;
    }
  };

  const userProfileImage = watch("profile_image");

  const userProfileImageURL = useMemo(() => {
    if (!userProfileImage) return undefined;
    return URL.createObjectURL(userProfileImage);
  }, [userProfileImage]);

  const [userProfileImageHash, isUserProfileHashLoading] = useBlurHashEncoder(userProfileImageURL);

  const handleBackButtonClick = () => {
    navigate(-1);
  };

  const handleCancelButtonClick = () => {
    navigate(-1);
  };

  const handleProfileImageUploadToS3 = async (file: any) => {
    if (!file) return null;
    const uploadedImage = await put<any>(UPLOAD_TO_S3, appendJsonToFormData(file));
    if (uploadedImage?.file_url) {
      return uploadedImage.file_url;
    }
    return toast.error("Failed to upload image", TOAST_CONFIG);
  };

  //   function for posting data to the server
  const handleUserProfileDetailsUpdate = async (data: UserProfileSettingForm): Promise<any> => {
    setLoading(true);

    const profileImageUrl = data.profile_image ? await handleProfileImageUploadToS3(data.profile_image) : undefined;

    //  function for handling profile update(will be passed to patch request as parameter)
    const handleProfileUpdateSuccess = () => {
      toast.success("User Profile Details Updated Successfully!", TOAST_CONFIG);
      setLoading(false);
      navigate(ROUTES.USER_PROFILE_PAGE);
      reloadUserData();
    };

    //  function for handling profile update error(will be passed to patch request as parameter)
    const handleProfileUpdateError = (errorMessage: string, error: any, errorData: any) => {
      toast.error(errorMessage, TOAST_CONFIG);
      const step = handleSetErrorAndSwitchFormState<FormFieldsType>(errorData, formFields, setError);
      if (step) handleSetFormStep(step);
      setLoading(false);
    };

    const payload = {
      first_name: data.first_name,
      last_name: data.last_name,
      profile_image_url: profileImageUrl,
      profile_image_hash: userProfileImageHash,
      username: data.username,
      twitter_link: data.twitter_link,
      instagram_link: data.instagram_link,
      other_link: data.other_link,
      address_line1: data.address_line1,
      address_line2: data.address_line2,
      country: data.country,
      state: data.state,
      city: data.city,
      zipcode: data.zipcode,
    };
    // @ts-ignore
    const formData = prependHTTPsForSocialLinks(payload);

    patch(USER_SELF, formData, {
      processData: handleProfileUpdateSuccess,
      processError: handleProfileUpdateError,
    });
  };

  const handlePasswordUpdate = async (data: UpdatePassword): Promise<any> => {
    setLoading(true);

    const handleUpdateSuccess = (response: any) => {
      setTokenId(response.data.token);
      toast.success("Password Updated Successfully!", TOAST_CONFIG);
      setLoading(false);
      navigate(ROUTES.USER_PROFILE_PAGE);
    };

    //  function for handling profile update error(will be passed to patch request as parameter)
    const handleUpdateError = (errorMessage: string, error: any, errorData: any) => {
      toast.error(errorMessage, TOAST_CONFIG);
      const step = handleSetErrorAndSwitchFormState<FormFieldsType>(errorData, formFields, setError);
      if (step) handleSetFormStep(step);
      setLoading(false);
    };

    const payload = {
      old_password: data.old_password,
      new_password: data.new_password,
    };

    post(getChangePasswordURL, payload, {
      processData: handleUpdateSuccess,
      processError: handleUpdateError,
    });
  };

  const onSubmit = (data: UserProfileSettingForm) => {
    if (currentStep === 2) {
      handlePasswordUpdate(data);
    } else {
      handleUserProfileDetailsUpdate(data);
    }
  };

  const handleValidationAndSetFormStep = async (step: string, replace?: boolean) => {
    // triggering validation for current form and setting formstep incase of no errors
    if (currentStep === 2 || (await trigger())) {
      handleSetFormStep(step, replace);
    }
  };

  return (
    <>
      <Helmet>
        <title>Profile Settings - MAGNETIQ</title>
      </Helmet>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...formMethods}>
        <UserSettingsPage
          isUserProfileHashLoading={isUserProfileHashLoading}
          currentStep={currentStep}
          handleBackButtonClick={handleBackButtonClick}
          handleSetFormStep={handleValidationAndSetFormStep}
          handleSubmit={handleSubmit(onSubmit)}
          handleCancelButtonClick={handleCancelButtonClick}
          form={getForm(currentStep)}
        />
      </FormProvider>
    </>
  );
}

export default function ProfileSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentStep = Number(searchParams.get("step"));
  const stepFromUrl = searchParams.get("step");
  const navigate = useNavigate();
  const { reloadUserData, userDetails, loading } = useContext(UserContext);

  const handleSetFormStep = (step: string, replace?: boolean) => {
    searchParams.set("step", step);
    setSearchParams(searchParams, {
      replace,
    });
  };

  useEffect(() => {
    if (stepFromUrl === null) {
      handleSetFormStep("0", true);
    }
  }, []);

  const userDetailsWithRemovedHttpsForSocialLinks = removeHTTPsForSocialLinks({ ...userDetails });

  if (!loading && !userDetails) return null;

  return (
    <UserProfileSetting
      currentStep={currentStep}
      handleSetFormStep={handleSetFormStep}
      navigate={navigate}
      defaultValues={userDetailsWithRemovedHttpsForSocialLinks}
      reloadUserData={reloadUserData}
    />
  );
}
